<template>
  <div class="customer-service-wrapper">
    <div class="customer-service-container">
      <!-- 客服头部 -->
      <div class="cs-header">
        <h3>客服工作台</h3>
        <div class="header-info">
          <div class="online-count">
            在线用户: {{ onlineCount }}
          </div>
          <div class="service-status" :class="{ online: isConnected }">
            {{ isConnected ? '服务在线' : '服务离线' }}
          </div>
          <el-button size="small" @click="refreshSessions">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </div>

      <!-- 会话列表和聊天区域 -->
      <div class="cs-main">
        <!-- 会话列表 -->
        <div class="session-list">
<!--          <div class="session-list-header">-->
<!--            <h4>活跃会话</h4>-->
<!--            <span class="online-count">{{ onlineCount }} 个在线会话</span>-->
<!--          </div>-->

          <div class="session-items">
            <div
                v-for="session in onlineSessions"
                :key="session.sessionId"
                class="session-item"
                :class="{
                  active: selectedSession?.sessionId === session.sessionId,
                  unread: hasUnreadMessages(session.sessionId)
                }"
                @click="selectSession(session)"
            >
              <div class="session-avatar">
                <el-avatar :size="40" :src="getUserAvatar(session)">
                  <el-icon v-if="!getUserAvatar(session)"><Avatar /></el-icon>
                  <span v-else>{{ getUserInitial(session) }}</span>
                </el-avatar>
                <!-- 在线状态指示器 -->
                <div class="online-indicator" :class="{ online: session.isOnline }"></div>
                <!-- 未读计数标识 - 移到头像容器内 -->
                <div class="unread-count-badge" v-if="getUnreadCount(session.sessionId) > 0">
                  {{ getUnreadCount(session.sessionId) > 99 ? '99+' : getUnreadCount(session.sessionId) }}
                </div>
              </div>

              <div class="session-info">
                <div class="user-identity">
                  <div class="user-info-left">
                    <span class="user-type" :class="{ permanent: session.isPermanent }">
                      {{ session.isPermanent ? '注册用户' : '访客' }}
                    </span>
                    <span class="user-id">
                      {{ session.userId ? '用户' + session.userId.substring(session.userId.length - 5) : '访客' + session.visitorId.substring(session.visitorId.length - 5) }}
                    </span>
                  </div>
                  <!-- 时间 -->
                  <span class="last-time">
                    {{ formatTime(session.lastActivityTime) }}
                  </span>
                </div>

                <div class="last-message" v-if="session.lastMessage">
                  {{ session.lastMessage }}
                </div>

              </div>

<!--              <div class="session-badge" v-if="getUnreadCount(session.sessionId) > 0">-->
<!--                <div class="unread-count-badge">-->
<!--                  {{ getUnreadCount(session.sessionId) > 99 ? '99+' : getUnreadCount(session.sessionId) }}-->
<!--                </div>-->
<!--              </div>-->
            </div>
          </div>

          <div v-if="onlineSessions.length === 0" class="no-sessions">
            <el-empty description="暂无活跃会话" />
          </div>
        </div>

        <!-- 聊天区域 -->
        <div class="chat-area">
          <div v-if="selectedSession" class="chat-container">
            <!-- 聊天头部 -->
            <div class="chat-header">
              <div class="chat-user-info">
                <el-avatar :size="40" :src="getUserAvatar(selectedSession)">
                  <el-icon v-if="!getUserAvatar(selectedSession)"><Avatar /></el-icon>
                  <span v-else>{{ getUserInitial(selectedSession) }}</span>
                </el-avatar>
                <div class="user-details">
                  <!-- 第一行：用户ID和用户类型 -->
                  <div class="first-line">
                    <h4>
                      {{ selectedSession.userId ? '用户 ' + selectedSession.userId : '访客 ' + selectedSession.visitorId }}
                    </h4>
                    <span class="user-type" :class="{ permanent: selectedSession.isPermanent }">
                      {{ selectedSession.isPermanent ? '注册用户' : '访客' }}
                    </span>
                  </div>

                  <!-- 第二行：其他信息 -->
                  <div class="session-info-header">
                    <span>IP: {{ selectedSession.ipAddress }}</span>
                    <span>开始时间: {{ formatTime(selectedSession.startTime) }}</span>
                    <span :class="{ online: selectedSession.isOnline }">
                      {{ selectedSession.isOnline ? '在线' : '离线' }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="chat-actions">
                <el-button size="small" @click="endSelectedSession">
                  结束会话
                </el-button>
              </div>
            </div>

            <!-- 消息区域 -->
            <div class="chat-messages" ref="csMessagesContainer">
              <div v-for="(message, index) in displayMessages"
                   :key="message.timestamp + (message.isRead ? '_read' : '_unread')">

                <!-- 时间分隔条 -->
                <div v-if="message.type === 'timeDivider'" class="time-divider">
                  <span>{{ formatTimeDivider(message.time) }}</span>
                </div>

                <!-- 消息内容 -->
                <div v-else class="message" :class="getMessageClass(message)">
                  <div class="message-avatar">
                    <el-avatar :size="32" :src="getMessageAvatar(message)">
                      <el-icon v-if="!getMessageAvatar(message)">
                        <Avatar/>
                      </el-icon>
                      <span v-else>{{ getMessageInitial(message) }}</span>
                    </el-avatar>
                  </div>
                  <div class="message-content-wrapper">
                    <div class="message-content">{{ message.content }}</div>

                    <!-- 只在最后一条客服消息显示已读状态 -->
                    <div v-if="showReadStatus(message, index)" class="message-status">
                      <span class="read-status">
                        {{ getReadStatusText(message) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="currentMessages.length === 0" class="no-messages">
                <p>暂无消息记录</p>
              </div>
            </div>

            <!-- 输入区域 -->
            <div class="chat-input-area">
              <div class="input-with-button">
                <el-input
                    v-model="csInputMessage"
                    :placeholder="isInputDisabled ? '访客当前离线，无法发送消息' : '回复用户...'"
                    @keyup.enter="sendCsMessage"
                    :rows="3"
                    type="textarea"
                    :disabled="isInputDisabled"
                    class="message-input"
                />
                <el-button
                    @click="sendCsMessage"
                    :disabled="!csInputMessage.trim() || isInputDisabled"
                    type="primary"
                    class="send-button"
                >
                  发送
                </el-button>
              </div>
              <div v-if="selectedSession && !selectedSession.isOnline && !isInputDisabled" class="offline-notice">
                用户当前离线，消息将在用户上线后送达
              </div>
            </div>
          </div>

          <div v-else class="no-session-selected">
            <el-empty description="请选择一个会话开始聊天" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh, Avatar } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 客服相关数据
const onlineSessions = ref([])
const selectedSession = ref(null)
const currentMessages = ref([])
const csInputMessage = ref('')
const csMessagesContainer = ref(null)
const isConnected = ref(false)
const unreadSessions = ref(new Map())

// WebSocket相关
let socket = null
let reconnectTimer = null
let heartbeatTimer = null

const onlineCount = computed(() => {
  return onlineSessions.value.filter(session => session.isOnline).length
})

// 加载客服会话列表
const loadSessions = async () => {
  try {
    // console.log('开始加载增强版客服会话列表...')
    const response = await request.get('/im/customer-service/sessions/enhanced')
    // console.log('增强版客服会话列表响应:', response)
    if (response.code === '200') {
      onlineSessions.value = response.data
      // console.log('加载到的会话数量:', onlineSessions.value.length)

      // 按最后活动时间排序
      onlineSessions.value.sort((a, b) => new Date(b.lastActivityTime) - new Date(a.lastActivityTime))

      // 重新初始化未读计数 - 从后端获取每个会话的未读消息数量
      await initUnreadCounts()

      // 打印每个会话的详细信息
      onlineSessions.value.forEach(session => {
        // console.log(`会话 ${session.sessionId}:`, {
        //   userId: session.userId,
        //   visitorId: session.visitorId,
        //   lastMessage: session.lastMessage,
        //   isOnline: session.isOnline,
        //   isPermanent: session.isPermanent,
        //   mergedSessionCount: session.mergedSessionCount,
        //   status: session.status
        // })
      })
    } else {
      console.error('加载会话列表失败:', response.message)
      // ElMessage.error('加载会话列表失败')
    }
  } catch (error) {
    console.error('加载会话列表失败:', error)
    // ElMessage.error('加载会话列表失败')
  }
}

// 初始化未读计数 - 从后端获取每个会话的未读消息数量
const initUnreadCounts = async () => {
  try {
    // 清空当前的未读计数
    unreadSessions.value.clear()

    // 为每个会话获取未读消息数量
    for (const session of onlineSessions.value) {
      const unreadCount = await getSessionUnreadCount(session.sessionId)
      if (unreadCount > 0) {
        unreadSessions.value.set(session.sessionId, unreadCount)
      }
    }
    // console.log('未读计数初始化完成:', Array.from(unreadSessions.value.entries()))
  } catch (error) {
    console.error('初始化未读计数失败:', error)
  }
}

// 获取会话的未读消息数量
const getSessionUnreadCount = async (sessionId) => {
  try {
    const response = await request.get('/im/messages/unread-count', {
      params: {
        sessionId: sessionId,
        userType: 'CUSTOMER_SERVICE'
      }
    })
    if (response.code === '200') {
      return response.data || 0
    }
    return 0
  } catch (error) {
    console.error('获取未读消息数量失败:', error)
    return 0
  }
}

// 选择会话
const selectSession = async (session) => {
  selectedSession.value = session
  currentMessages.value = []

  // 标记为已读 - 只有主动选择会话时才执行
  await markSessionAsRead(session)

  // 从服务器获取最新的未读计数并清除前端的未读计数
  await updateUnreadCountFromServer(session.sessionId)

  // 如果服务器返回0，则清除本地计数
  const latestUnreadCount = await getSessionUnreadCount(session.sessionId)
  if (latestUnreadCount === 0) {
    unreadSessions.value.delete(session.sessionId)
  }

  try {
    // console.log('客服端选择会话:', {
    //   sessionId: session.sessionId,
    //   userId: session.userId,
    //   isPermanent: session.isPermanent,
    //   mergedSessionCount: session.mergedSessionCount
    // })

    let params = {}
    if (session.isPermanent && session.userId) {
      // 登录用户：获取该用户所有会话的历史消息
      params.userId = session.userId
      console.log('加载登录用户所有历史消息 - userId:', session.userId)
    } else if (session.visitorId) {
      // 访客：获取该访客所有会话的历史消息
      params.visitorId = session.visitorId
      console.log('加载访客所有历史消息 - visitorId:', session.visitorId)
    } else {
      // 备用：获取当前会话的历史消息
      params.sessionId = session.sessionId
      console.log('加载会话历史消息 - sessionId:', session.sessionId)
    }

    const response = await request.get('/im/customer-service/history', { params })
    if (response.code === '200') {
      // 确保消息包含已读状态
      currentMessages.value = response.data.map(msg => ({
        ...msg,
        isRead: msg.isRead !== undefined ? msg.isRead : (msg.fromUserType === 'CUSTOMER_SERVICE' ? false : true)
      }))
      scrollToBottom()
    } else {
      console.error('客服端加载历史消息失败:', response.message)
    }
  } catch (error) {
    console.error('客服端加载历史消息失败:', error)
  }
}

// 标记会话为已读
const markSessionAsRead = async (session) => {
  try {
    if (isConnected.value) {
      const markReadMsg = {
        type: 'mark_read',
        sessionId: session.sessionId,
        userType: 'CUSTOMER_SERVICE',
        // userId: '1972196479572934658',
        isPermanentUser: session.isPermanent,
        timestamp: Date.now()
      }
      socket.send(JSON.stringify(markReadMsg))
      // console.log('已发送消息已读通知', session.isPermanent ? '(登录用户，将标记所有会话)' : '(访客用户)')
    }
  } catch (error) {
    console.error('标记会话为已读失败:', error)
  }
}

// 客服发送消息
const sendCsMessage = () => {
  if (!csInputMessage.value.trim() || !selectedSession.value) return

  const messageContent = csInputMessage.value.trim()
  const message = {
    type: 'message',
    sessionId: selectedSession.value.sessionId,
    content: messageContent,
    userType: 'CUSTOMER_SERVICE',
    timestamp: Date.now()
  }

  // console.log('客服发送消息:', message);

  socket.send(JSON.stringify(message))

  // 立即在界面显示发送的消息
  const newMessage = {
    content: messageContent,
    fromUserType: 'CUSTOMER_SERVICE',
    sendTime: new Date(),
    timestamp: Date.now(),
    sessionId: selectedSession.value.sessionId,
    isRead: false // 客服发送的消息默认未读
  }
  currentMessages.value.push(newMessage)

  // 立即更新会话列表的最后一条消息
  updateSessionLastMessage(selectedSession.value.sessionId, messageContent)

  csInputMessage.value = ''
  scrollToBottom()
}


// 更新会话的最后一条消息
const updateSessionLastMessage = (sessionId, messageContent) => {
  const sessionIndex = onlineSessions.value.findIndex(session => session.sessionId === sessionId)
  if (sessionIndex !== -1) {
    // 创建新的会话对象以避免响应性问题
    const updatedSession = {
      ...onlineSessions.value[sessionIndex],
      lastMessage: messageContent,
      lastActivityTime: Date.now()
    }

    // 替换原会话对象
    onlineSessions.value.splice(sessionIndex, 1, updatedSession)

    // 如果当前选中的会话就是被更新的会话，也更新selectedSession
    if (selectedSession.value && selectedSession.value.sessionId === sessionId) {
      selectedSession.value = updatedSession
    }

    // 按最后活动时间重新排序会话列表（最新的在最上面）
    onlineSessions.value.sort((a, b) => new Date(b.lastActivityTime) - new Date(a.lastActivityTime))
  }
}

// 结束选中的会话
const endSelectedSession = async () => {
  if (!selectedSession.value) return

  try {
    await ElMessageBox.confirm('确定要结束这个会话吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    await request.post('/im/session/end', null, {
      params: { sessionId: selectedSession.value.sessionId }
    })

    // ElMessage.success('会话已结束')

    // 从列表中移除
    onlineSessions.value = onlineSessions.value.filter(
        session => session.sessionId !== selectedSession.value.sessionId
    )
    selectedSession.value = null
    currentMessages.value = []

  } catch (error) {
    if (error !== 'cancel') {
      console.error('结束会话失败:', error)
      // ElMessage.error('结束会话失败')
    }
  }
}

// 刷新会话列表
const refreshSessions = () => {
  loadSessions()
}

// 检查会话是否有未读消息 - 返回数量
const getUnreadCount = (sessionId) => {
  return unreadSessions.value.get(sessionId) || 0
}

// 从服务器更新未读计数
const updateUnreadCountFromServer = async (sessionId) => {
  try {
    const unreadCount = await getSessionUnreadCount(sessionId)
    unreadSessions.value.set(sessionId, unreadCount)
    // console.log('更新会话未读计数 - sessionId:', sessionId, '未读数量:', unreadCount)
  } catch (error) {
    console.error('更新未读计数失败:', error)
  }
}

// 检查会话是否有未读消息
const hasUnreadMessages = (sessionId) => {
  return unreadSessions.value.has(sessionId)
}

// 获取用户头像
const getUserAvatar = (session) => {
  // 如果后端返回的头像不为空，则使用后端头像
  if (session.avatar) {
    return session.avatar;
  }
  // 否则返回 null，在模板中使用图标
  return null;
}

// 获取用户首字母
const getUserInitial = (session) => {
  return session.isPermanent ? 'U' : 'V'
}

// 获取消息头像
const getMessageAvatar = (message) => {
  // 如果消息中有头像且不为空，使用消息头像
  if (message.avatar) {
    return message.avatar;
  }
  // 否则根据用户类型返回 null
  return null;
}

// 获取消息首字母
const getMessageInitial = (message) => {
  return message.fromUserType === 'CUSTOMER_SERVICE' ? 'C' : 'U'
}

// 处理用户上线
const handleUserOnline = (data) => {
  // console.log('用户上线:', data.userKey);

  // 更新会话列表中的在线状态
  updateSessionOnlineStatus(data.userKey, true, data);

  // 如果当前选中的会话就是这个用户，更新选中会话的状态
  if (selectedSession.value && getSessionUserKey(selectedSession.value) === data.userKey) {
    selectedSession.value.isOnline = true;
  }

  // ElMessage.success(`${getUserDisplayName(data)} 已上线`);
}

// 处理用户下线
const handleUserOffline = (data) => {
  // console.log('用户下线:', data.userKey);

  // 更新会话列表中的在线状态
  updateSessionOnlineStatus(data.userKey, false, data);

  // 如果当前选中的会话就是这个用户，更新选中会话的状态
  if (selectedSession.value && getSessionUserKey(selectedSession.value) === data.userKey) {
    selectedSession.value.isOnline = false;
  }

  // ElMessage.warning(`${getUserDisplayName(data)} 已下线`);
}

// 处理消息已读通知
const handleMessagesRead = (data) => {
  // console.log('收到消息已读通知:', data)
  // 用户已读客服的消息，可以更新界面状态
  if (data.readerUserType !== 'CUSTOMER_SERVICE' && selectedSession.value &&
      selectedSession.value.sessionId === data.sessionId) {
    // 可以在这里添加界面更新逻辑，比如显示"用户已读"状态
    // ElMessage.success('用户已阅读您的消息')
  }
}

// 处理实时已读状态更新
const handleReadStatusUpdate = (data) => {
  // console.log('收到实时已读状态更新:', data)

  // 只有当更新的是当前选中的会话时才处理
  if (selectedSession.value && selectedSession.value.sessionId === data.sessionId) {
    // 更新消息列表中的已读状态
    updateMessagesReadStatus(data)

    // 显示通知
    if (data.readerUserType !== 'CUSTOMER_SERVICE') {
    }
  }
}

// 更新消息列表中的已读状态
const updateMessagesReadStatus = (data) => {
  // 找到最后一条客服消息并标记为已读
  for (let i = currentMessages.value.length - 1; i >= 0; i--) {
    const message = currentMessages.value[i]

    // 如果是客服发送的消息且未读，标记为已读
    if (message.fromUserType === 'CUSTOMER_SERVICE' && !message.isRead) {
      message.isRead = true
      message.readTime = data.timestamp ? new Date(data.timestamp) : new Date()

      // 触发响应式更新
      currentMessages.value.splice(i, 1, {...message})
      break
    }
  }
}

// 根据会话信息生成 userKey
const getSessionUserKey = (session) => {
  if (session.isPermanent && session.userId) {
    return `USER_${session.userId}`;
  } else {
    return `VISITOR_${session.sessionId}`;
  }
}

const getUserDisplayName = (data) => {
  if (data.userId) {
    return `用户${data.userId.substring(data.userId.length - 5)}`;
  } else if (data.sessionId) {
    return `访客${data.sessionId.substring(data.sessionId.length - 5)}`;
  } else {
    return data.userKey;
  }
}

// 更新会话在线状态
const updateSessionOnlineStatus = (userKey, isOnline, data) => {
  const sessionIndex = onlineSessions.value.findIndex(session =>
      getSessionUserKey(session) === userKey
  );

  if (sessionIndex !== -1) {
    const updatedSession = {
      ...onlineSessions.value[sessionIndex],
      isOnline: isOnline,
      lastActivityTime: data.timestamp || Date.now()
    }

    onlineSessions.value.splice(sessionIndex, 1, updatedSession);

    if (selectedSession.value && selectedSession.value.sessionId === updatedSession.sessionId) {
      selectedSession.value = updatedSession;
    }

    onlineSessions.value.sort((a, b) => new Date(b.lastActivityTime) - new Date(a.lastActivityTime));
  } else if (isOnline && data.sessionId) {
    // 新用户上线，重新加载会话列表
    loadSessions();
  }
}

// WebSocket连接
const connectWebSocket = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsUrl = `ws://localhost:8088/ws`;

  // console.log('客服正在连接WebSocket:', wsUrl)
  socket = new WebSocket(wsUrl)

  socket.onopen = () => {
    isConnected.value = true
    // console.log('客服WebSocket连接成功')

    // 发送客服连接消息
    const connectMsg = {
      type: 'connect',
      userType: 'CUSTOMER_SERVICE',
      // userId: '1972196479572934658'
    }
    socket.send(JSON.stringify(connectMsg))
    // console.log('客服连接消息已发送')

    // 开始心跳检测
    startHeartbeat()
  }

  socket.onmessage = (event) => {
    // console.log('客服收到WebSocket消息:', event.data)
    const data = JSON.parse(event.data)
    handleWebSocketMessage(data)
  }

  socket.onclose = (event) => {
    isConnected.value = false
    // console.log('客服WebSocket连接关闭:', event.code, event.reason)

    // 清理心跳
    stopHeartbeat()

    // 如果不是正常关闭，尝试重连
    if (event.code !== 1000) {
      scheduleReconnect()
    }
  }

  socket.onerror = (error) => {
    // console.error('客服WebSocket错误:', error)
    isConnected.value = false
    stopHeartbeat()
  }
}

// 处理WebSocket消息
const handleWebSocketMessage = (data) => {
  switch (data.type) {
    case 'message':
      // 收到用户消息
      console.log('客服收到用户消息:', data);

      if (selectedSession.value && data.sessionId === selectedSession.value.sessionId) {
        // 当前选中的会话，直接显示消息
        const newMessage = {
          content: data.content,
          fromUserType: data.fromUserType,
          sendTime: data.timestamp ? new Date(data.timestamp) : new Date(),
          isRead: data.fromUserType === 'CUSTOMER_SERVICE' ? false : true,
          timestamp: data.timestamp || Date.now(),
          sessionId: data.sessionId
        }
        currentMessages.value.push(newMessage)
        scrollToBottom()

        // 更新会话的最后一条消息
        updateSessionLastMessage(data.sessionId, data.content)

        // 自动标记用户消息为已读（因为客服正在查看）
        if (data.fromUserType !== 'CUSTOMER_SERVICE') {
          handleUserMessageAutoRead(data)
        }
      } else {
        // 其他会话的消息，增加未读计数
        const currentCount = unreadSessions.value.get(data.sessionId) || 0
        unreadSessions.value.set(data.sessionId, currentCount + 1)

        // 更新会话的最后一条消息
        updateSessionLastMessage(data.sessionId, data.content)

        // 重新从后端获取准确的未读计数
        updateUnreadCountFromServer(data.sessionId)

        // 收到新消息时刷新会话列表
        loadSessions()
      }
      break

    case 'user_connected':
      // 新用户连接，刷新会话列表
      loadSessions()
      break

    case 'user_online':
      handleUserOnline(data);
      break

    case 'user_offline':
      handleUserOffline(data);
      break

    case 'messages_read':
      // 处理消息已读通知
      handleMessagesRead(data);
      break;

    case 'read_status_update':
      // 实时已读状态更新
      handleReadStatusUpdate(data);
      break;

    case 'success':
      console.log('操作成功:', data.message)
      break

    case 'error':
      ElMessage.error(data.message)
      break
  }
}

// 心跳检测
const startHeartbeat = () => {
  heartbeatTimer = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const heartbeatMsg = {
        type: 'heartbeat',
        userType: 'CUSTOMER_SERVICE',
        timestamp: Date.now()
      }
      socket.send(JSON.stringify(heartbeatMsg))
    }
  }, 30000) // 30秒一次心跳
}

const stopHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

// 工具函数
const getMessageClass = (message) => {
  return {
    'customer-service': message.fromUserType === 'CUSTOMER_SERVICE',
    'user': message.fromUserType !== 'CUSTOMER_SERVICE'
  }
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  return new Date(timestamp).toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 智能格式化消息时间
const formatMessageTime = (timestamp) => {
  if (!timestamp) return ''

  const messageDate = new Date(timestamp)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate())

  // 如果是今天，显示时间
  if (messageDay.getTime() === today.getTime()) {
    return messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  // 如果是昨天，显示"昨天 + 时间"
  else if (messageDay.getTime() === yesterday.getTime()) {
    return `昨天 ${messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })}`
  }
  // 如果是更早的时间，显示完整日期和时间
  else {
    return messageDate.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/\//g, '-')
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (csMessagesContainer.value) {
      csMessagesContainer.value.scrollTop = csMessagesContainer.value.scrollHeight
    }
  })
}

const scheduleReconnect = () => {
  if (reconnectTimer) clearTimeout(reconnectTimer)
  reconnectTimer = setTimeout(() => {
    console.log('客服尝试重新连接...')
    connectWebSocket()
  }, 3000)
}

// 检查是否显示已读状态
const showReadStatus = (message, index) => {
  // 只在最后一条客服消息显示已读状态
  const isLastMessage = index === displayMessages.value.length - 1;
  const isCustomerMessage = message.fromUserType === 'CUSTOMER_SERVICE';

  // 检查是否是当天
  const isToday = (timestamp) => {
    if (!timestamp) return false;
    const messageDate = new Date(timestamp);
    const today = new Date();
    return messageDate.getDate() === today.getDate() &&
        messageDate.getMonth() === today.getMonth() &&
        messageDate.getFullYear() === today.getFullYear();
  };

  return isLastMessage && isCustomerMessage && message.isRead && isToday(message.sendTime || message.timestamp);
};

// 获取已读状态文本
const getReadStatusText = (message) => {
  if (!message.isRead) return '';
  return '已读';
};

// 处理用户发送的消息自动标记已读
const handleUserMessageAutoRead = (data) => {
  // 如果是用户发送的消息，且当前正在查看该会话，自动标记为已读
  if (data.fromUserType !== 'CUSTOMER_SERVICE' &&
      selectedSession.value &&
      selectedSession.value.sessionId === data.sessionId) {

    // console.log('自动标记用户消息为已读')
    const markReadMsg = {
      type: 'mark_read',
      sessionId: data.sessionId,
      userType: 'CUSTOMER_SERVICE',
      // userId: '1972196479572934658',
      timestamp: Date.now()
    }
    socket.send(JSON.stringify(markReadMsg))
  }
}

// 处理带时间分隔条的消息列表
const displayMessages = computed(() => {
  if (currentMessages.value.length === 0) return [];

  const messagesWithDividers = [];
  const TIME_INTERVAL = 30 * 60 * 1000; // 5分钟

  // 检查是否为同一天
  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
  };

  // 检查是否为今天
  const isToday = (date) => {
    const today = new Date();
    const targetDate = new Date(date);
    return targetDate.getFullYear() === today.getFullYear() &&
        targetDate.getMonth() === today.getMonth() &&
        targetDate.getDate() === today.getDate();
  };

  // 添加第一条消息的时间分隔条
  const firstMessageTime = currentMessages.value[0].sendTime || currentMessages.value[0].timestamp;
  messagesWithDividers.push({
    type: 'timeDivider',
    time: firstMessageTime
  });

  for (let i = 0; i < currentMessages.value.length; i++) {
    const currentMessage = currentMessages.value[i];
    const currentTime = new Date(currentMessage.sendTime || currentMessage.timestamp);

    // 添加消息
    messagesWithDividers.push(currentMessage);

    // 检查是否需要添加时间分隔条
    if (i < currentMessages.value.length - 1) {
      const nextMessage = currentMessages.value[i + 1];
      const nextTime = new Date(nextMessage.sendTime || nextMessage.timestamp);

      // 如果是同一天且是今天，使用5分钟间隔
      if (isSameDay(currentTime, nextTime) && isToday(currentTime)) {
        if (Math.abs(nextTime.getTime() - currentTime.getTime()) > TIME_INTERVAL) {
          messagesWithDividers.push({
            type: 'timeDivider',
            time: nextMessage.sendTime || nextMessage.timestamp
          });
        }
      }
      // 如果不是同一天，在跨天的第一条消息前添加分隔条
      else if (!isSameDay(currentTime, nextTime)) {
        messagesWithDividers.push({
          type: 'timeDivider',
          time: nextMessage.sendTime || nextMessage.timestamp
        });
      }
    }
  }

  return messagesWithDividers;
});

// 格式化时间分隔条显示
const formatTimeDivider = (timestamp) => {
  if (!timestamp) return '';

  const messageDate = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

  if (messageDay.getTime() === today.getTime()) {
    return `今天 ${messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  } else if (messageDay.getTime() === yesterday.getTime()) {
    return `昨天 ${messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  } else {
    // 非今天和昨天的消息，只显示完整日期，不显示时间
    return messageDate.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  }
};

// 计算是否禁用输入框：只有访客会话且不在线时才禁用
const isInputDisabled = computed(() => {
  if (!selectedSession.value) return true;

  // 如果是登录用户，无论在线离线都可以发送消息
  if (selectedSession.value.isPermanent) {
    return false;
  }

  // 如果是访客且不在线，才禁用输入框
  return !selectedSession.value.isOnline;
});

// 初始化客服工作台
const initCustomerService = async () => {
  try {
    await loadSessions()
    connectWebSocket()
    ElMessage.success('客服工作台已就绪')
  } catch (error) {
    console.error('初始化客服工作台失败:', error)
    ElMessage.error('初始化客服工作台失败')
  }
}

// 生命周期
onMounted(() => {
  initCustomerService()
})

onUnmounted(() => {
  // 组件销毁时清理资源
  stopHeartbeat()

  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
  }

  if (socket) {
    socket.close(1000, '页面关闭')
  }
})
</script>

<style scoped>
.customer-service-wrapper {
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex: 1;
  padding: 20px;
  background: #f5f7fa;
  box-sizing: border-box;
  min-height: calc(100vh - 140px); /* 确保容器不会超过视口高度 */
  max-height: calc(100vh - 120px); /* 添加最大高度限制 */
  overflow: hidden; /* 隐藏容器自身的滚动 */
}

.customer-service-container {
  width: 90%;
  max-width: 1400px;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex: 1; /* 容器也填满父级 */
  min-height: 0; /* 允许收缩 */
}

.cs-header {
  padding: 16px 20px;
  background: #409EFF;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.online-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
}

.service-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.2);
}

.service-status.online {
  background: #67C23A;
}

.cs-main {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.session-list {
  width: 300px;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.session-list-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 80px; /* 添加固定高度，与chat-header保持一致 */
  min-height: 80px; /* 确保最小高度 */
  box-sizing: border-box; /* 确保padding包含在高度内 */
}

.session-list-header h4 {
  margin: 0 0 8px 0;
  color: #303133;
}

.session-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #909399;
}

.session-items {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.session-items::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.session-item {
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.3s;
  display: flex;
  align-items: flex-start; /* 改为顶部对齐 */
  gap: 12px;
  position: relative;
}

.session-item:hover {
  background: #f5f7fa;
}

.session-item.active {
  background: #ecf5ff;
  border-right: 3px solid #409EFF;
}

.session-item.unread {
  background: #fff8f8;
}

.session-avatar {
  position: relative;
  flex-shrink: 0; /* 防止头像被压缩 */
}

/* 添加头像圆角样式 */
.session-avatar :deep(.el-avatar) {
  border-radius: 6px;   /* 调整这个值来控制圆角大小 */
}

.online-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c0c4cc;
  position: absolute;
  bottom: 2px;
  right: 2px;
}

.online-indicator.online {
  background: #67C23A;
}

/* 未读计数标识样式  */
.unread-count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: #f56c6c;
  color: white;
  border-radius: 50%;
  min-width: 15px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  z-index: 2;
}

.session-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px; /* 减少间距 */
  padding-right: 30px;
}

.user-identity {
  display: flex;
  justify-content: space-between; /* 左右对齐 */
  align-items: center; /* 垂直居中 */
  width: 100%;
  margin-bottom: 0; /* 移除底部边距 */
}

.user-info-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1; /* 占据剩余空间 */
  min-width: 0; /* 允许收缩 */
}

.user-type {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 8px;
  background: #f0f0f0;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0; /* 防止压缩 */
}

.user-type.permanent {
  background: #e1f3d8;
  color: #67C23A;
}

.user-id {
  font-weight: bold;
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0; /* 允许收缩 */
}

/* 时间样式 */
.last-time {
  font-size: 11px;
  color: #c0c4cc;
  white-space: nowrap;
  flex-shrink: 0; /* 防止压缩 */
  margin-left: 8px; /* 与左侧内容保持间距 */
}

.last-message {
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  line-height: 1.3;
}


.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 聊天框头部保持原有样式 */
.chat-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.chat-user-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
}

.chat-user-info :deep(.el-avatar) {
  border-radius: 6px;   /* 调整这个值来控制圆角大小 */
}

.session-info-header {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  flex-wrap: wrap; /* 允许在小屏幕上换行 */
}

.session-info-header span.online {
  color: #67C23A;
  font-weight: 500;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 4px; /* 两行之间的间距 */
}

.first-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap; /* 允许在小屏幕上换行 */
}

.user-details h4 {
  margin: 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.user-details .session-info {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}

.user-details .session-info span.online {
  color: #67C23A;
  font-weight: 500;
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #f5f5f5;
  min-height: 0;

  /* 滚动条样式 */
  scrollbar-width: thin; /* Firefox */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-button {
    display: none;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.2);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
}

.message {
  display: flex;
  margin-bottom: 20px;
  gap: 5px;
}

.message.customer-service {
  flex-direction: row-reverse;
}

/* 时间分隔条样式 */
.time-divider {
  text-align: center;
  margin: 16px 0;
  position: relative;
}

.time-divider span {
  display: inline-block;

  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 12px;
  max-width: 70%;
  word-break: keep-all;
  white-space: nowrap;
}

/* 客服消息气泡 - 右侧，绿色 */
.message.customer-service .message-content {
  background: #95EC69;
  color: #000;
  border-radius: 3px;
  position: relative;
  margin-right: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 客服消息气泡的小箭头 */
.message.customer-service .message-content::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 10px;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-left-color: #95EC69;
  border-right: 0;
}

/* 用户消息气泡 - 左侧，白色 */
.message.user .message-content {
  background: #FFFFFF;
  color: #000;
  border-radius: 3px;
  position: relative;
  margin-left: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 用户消息气泡的小箭头 */
.message.user .message-content::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 10px;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-right-color: #FFFFFF;
  border-left: 0;
}

.message-content-wrapper {
  max-width: 45%;
}

.message-content {
  padding: 5px 12px;
  border-radius: 3px;
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 14px;
}

/* 消息状态区域 */
.message-status {
  text-align: right;
  margin-top: 4px;
  font-size: 11px;
  height: 16px;
}

.message.user .message-status {
  text-align: left;
}

.read-status {
  color: #67c23a;
  font-style: italic;
}

.no-messages {
  text-align: center;
  color: #909399;
  padding: 40px 0;
}


.chat-input-area {
  padding: 6px;
  border-top: 1px solid #cdcbcb;
  background: #f9f9f9;
  flex-shrink: 0;
}

.input-with-button {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.message-input {
  /* 移除边框 */
  border: none !important;
  /* 内边距 */
  padding: 12px !important;
}


/* 输入框聚焦时的样式 */
.message-input:deep(.el-textarea__inner) {
  border: none !important;
  background-color: #f9f9f9 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  resize: none !important;
  /* 设置光标颜色为绿色 */
  caret-color: #07C160 !important;
  font-size: 14px;
  line-height: 1.4;
  min-height: 60px !important;
}

/* 输入框聚焦时的边框颜色 */
.message-input:deep(.el-textarea__inner:focus) {
  border: none !important;
  box-shadow: none !important;
  background-color: #f9f9f9 !important;
}

/* 输入框悬停时的样式 */
.message-input:deep(.el-textarea__inner:hover) {
  border: none !important;

}

/* 禁用状态的样式 */
.message-input:deep(.el-textarea__inner:disabled) {
  background-color: #f9f9f9 !important;
  color: #c0c4cc !important;
}

.send-button {
  height: fit-content;
  white-space: nowrap;
  background-color: #07C160 !important;
  border-color: #07C160 !important;
}

.send-button:hover {
  background-color: #06ae56 !important;
  border-color: #06ae56 !important;
}

.send-button:disabled {
  background-color: #a0a0a0 !important;
  border-color: #a0a0a0 !important;
}

.offline-notice {
  font-size: 12px;
  color: #e6a23c;
  margin-top: 8px;
  text-align: center;
}

.no-session-selected {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
}
</style>